/**
 * tokenStorage — cookie-backed token manager.
 *
 * Cookies are used instead of localStorage so tokens benefit from
 * automatic expiry (browser discards them after max-age elapses).
 *
 * Security flags applied:
 *   - max-age  : token lifetime from the API response (seconds)
 *   - path=/   : accessible across the whole app
 *   - SameSite=Strict : CSRF protection
 *   - Secure   : added automatically on HTTPS (production)
 *
 * NOTE: tokens are NOT HttpOnly because they must be readable by JS
 * (the axios interceptor needs them). Server-side rendering setups
 * should rotate to HttpOnly server-set cookies instead.
 *
 * Used directly by the axios interceptor so it can read / write tokens
 * without importing the Zustand store (which would create a circular dep).
 */

const COOKIE_NAMES = {
  ACCESS: "auth_access_token",
  REFRESH: "auth_refresh_token",
} as const;

// ─── Low-level cookie helpers ─────────────────────────────────────────────────

function setCookie(name: string, value: string, maxAgeSeconds: number): void {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAgeSeconds}; path=/; SameSite=Strict${secure}`;
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] != null ? decodeURIComponent(match[1]) : null;
}

function removeCookie(name: string): void {
  // Setting max-age=0 tells the browser to delete the cookie immediately
  document.cookie = `${name}=; max-age=0; path=/; SameSite=Strict`;
}

// ─── Public API ───────────────────────────────────────────────────────────────

export const tokenStorage = {
  getAccessToken: (): string | null => getCookie(COOKIE_NAMES.ACCESS),
  getRefreshToken: (): string | null => getCookie(COOKIE_NAMES.REFRESH),

  setAccessToken: (token: string, expiresInSeconds: number): void =>
    setCookie(COOKIE_NAMES.ACCESS, token, expiresInSeconds),

  setRefreshToken: (token: string, expiresInSeconds: number): void =>
    setCookie(COOKIE_NAMES.REFRESH, token, expiresInSeconds),

  /** Set both tokens in one call — use the expiry values from the API response */
  setTokens: (
    accessToken: string,
    refreshToken: string,
    expiresIn: number,
    refreshTokenExpiresIn: number,
  ): void => {
    setCookie(COOKIE_NAMES.ACCESS, accessToken, expiresIn);
    setCookie(COOKIE_NAMES.REFRESH, refreshToken, refreshTokenExpiresIn);
  },

  clearTokens: (): void => {
    removeCookie(COOKIE_NAMES.ACCESS);
    removeCookie(COOKIE_NAMES.REFRESH);
  },
};
